/**
 * Owner, 2026-10-13: "add some kind of preview in setting". A template with a saved mock-up,
 * rendered with a fixed sample design at the print-area light values on screen, before Save.
 */
import { useEffect, useRef, useState } from "react";
import { composeArgsFor, type MockupAssets, type RenderBatchJob, type ShadingParams, type WireTemplateVersion } from "@genz/engine-bind";
import { useServices } from "../app/context.js";
import { SHADING_TUNED } from "../editor/useMockupBake.js";
import { getMockup } from "../stores/mockupStore.js";
import { forceSyncTemplate } from "../templates/forceSync.js";
import { chromaKeyFromPristine } from "../editor/chromaKey.js";
import { loadImagePixels } from "../editor/pixels.js";
import type { ProductTemplate } from "@genz/platform";
import { PRIMARY_VIEW } from "../templates/views.js";

export interface PreviewLight {
  print_shading: number;
  print_highlights: number;
  key_shadow_depth: number;
  key_highlight_depth: number;
  shading_smoothing: number;
  case_highlights: number;
  print_gradient: number;
  print_grain: number;
  print_sheen: number;
  print_black: number;
  print_white: number;
  key_by_purity: boolean;
  key_method: "spread" | "purity" | "bulk";
  key_noise_floor: number;
  key_snap_low: number;
  key_snap_high: number;
  key_flat_sample: number;
  key_tolerance: number;
  key_purity_start: number;
  key_purity_full: number;
  key_dark_keep: number;
  key_softness: number;
  key_colour_removal: number;
}

/** The template's own calibration with the five values on screen in place of its own. */
export function previewShading(saved: ShadingParams | undefined, v: PreviewLight): ShadingParams {
  return {
    ...SHADING_TUNED, ...saved,
    strength: v.print_shading, highlights: v.print_highlights,
    shadowBoost: v.key_shadow_depth, highlightBoost: v.key_highlight_depth, smoothing: v.shading_smoothing,
    caseHighlights: v.case_highlights,
    gradient: v.print_gradient, grain: v.print_grain, sheen: v.print_sheen,
    inkBlack: v.print_black, inkWhite: v.print_white,
  };
}

/** Black, white and three colours: a multiply shows on the light bands, the highlights on the dark. */
const SAMPLE_BANDS = ["#000000", "#ffffff", "#e0245e", "#1d6fe0", "#f5c518"];

export async function sampleDesign(): Promise<Uint8Array> {
  const canvas = document.createElement("canvas");
  canvas.width = 600;
  canvas.height = 1200;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no 2d canvas");
  const band = canvas.height / SAMPLE_BANDS.length;
  SAMPLE_BANDS.forEach((colour, i) => {
    ctx.fillStyle = colour;
    ctx.fillRect(0, i * band, canvas.width, band + 1);
  });
  const blob = await new Promise<Blob | null>((done) => canvas.toBlob(done, "image/png"));
  if (!blob) throw new Error("no image");
  return new Uint8Array(await blob.arrayBuffer());
}

/** Zoom limits of the preview; 1 is the whole picture in view. */
const MIN_ZOOM = 1;
const MAX_ZOOM = 8;

interface View { zoom: number; x: number; y: number }
const FIT: View = { zoom: 1, x: 0, y: 0 };

/**
 * Zoom and drag for the preview, by mouse, pen and touch (owner, 2026-10-14). The wheel and a
 * two-finger pinch zoom towards the pointer; one pointer drags; a double click fits again.
 */
function usePanZoom() {
  const [view, setView] = useState<View>(FIT);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<number | null>(null);
  const zoomAt = (factor: number, cx: number, cy: number) => setView((v) => {
    const zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, v.zoom * factor));
    if (zoom === MIN_ZOOM) return FIT;
    const k = zoom / v.zoom;
    return { zoom, x: cx - (cx - v.x) * k, y: cy - (cy - v.y) * k };
  });
  const local = (el: HTMLElement, x: number, y: number) => {
    const r = el.getBoundingClientRect();
    return { x: x - r.left - r.width / 2, y: y - r.top - r.height / 2 };
  };
  const handlers = {
    onWheel: (e: React.WheelEvent<HTMLDivElement>) => {
      const p = local(e.currentTarget, e.clientX, e.clientY);
      zoomAt(e.deltaY < 0 ? 1.2 : 1 / 1.2, p.x, p.y);
    },
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => {
      e.currentTarget.setPointerCapture?.(e.pointerId);
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    },
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => {
      const last = pointers.current.get(e.pointerId);
      if (!last) return;
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.current.size === 2) {
        const [a, b] = [...pointers.current.values()];
        const span = Math.hypot(a.x - b.x, a.y - b.y);
        const mid = local(e.currentTarget, (a.x + b.x) / 2, (a.y + b.y) / 2);
        if (pinch.current) zoomAt(span / pinch.current, mid.x, mid.y);
        pinch.current = span;
        return;
      }
      setView((v) => v.zoom === MIN_ZOOM ? v : { ...v, x: v.x + e.clientX - last.x, y: v.y + e.clientY - last.y });
    },
    onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => {
      pointers.current.delete(e.pointerId);
      if (pointers.current.size < 2) pinch.current = null;
    },
    onDoubleClick: () => setView(FIT),
  };
  return { view, handlers, zoomBy: (f: number) => zoomAt(f, 0, 0), fit: () => setView(FIT) };
}

/** The case image of a template's first view: the print guide image for a generated template. */
function caseImageRef(t: ProductTemplate): string | undefined {
  const ref = t.currentVersion?.payload?.views?.[0]?.base_case_image_ref;
  return typeof ref === "string" && /^(https?:|blob:|data:)/.test(ref) ? ref : undefined;
}

/**
 * The key view (owner, 2026-10-14: "add setting for both … with preview. make sure preview
 * works"): the template's own print guide image, keyed with the values on screen, over the
 * sample design. What is transparent shows the design; what the key keeps sits on top.
 */
async function keyedOverSample(url: string, v: PreviewLight, design: Uint8Array): Promise<Blob> {
  const { data, width, height } = await loadImagePixels(url);
  const keyed = chromaKeyFromPristine(data, width, height, true, {
    method: v.key_by_purity ? "purity" : v.key_method, tolerance: v.key_tolerance,
    noiseFloor: v.key_noise_floor, snapLow: v.key_snap_low, snapHigh: v.key_snap_high, flatSample: v.key_flat_sample,
    start: v.key_purity_start, full: v.key_purity_full, darkKeep: v.key_dark_keep,
    softness: v.key_softness, colour: v.key_colour_removal,
  });
  if (!keyed) throw new Error("no print guide in this image");
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no 2d canvas");
  const sample = await createImageBitmap(new Blob([design as BlobPart], { type: "image/png" }));
  ctx.drawImage(sample, 0, 0, width, height);
  const top = document.createElement("canvas");
  top.width = width;
  top.height = height;
  top.getContext("2d")!.putImageData(new ImageData(new Uint8ClampedArray(keyed.out), width, height), 0, 0);
  ctx.drawImage(top, 0, 0);
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("no image"))), "image/png"));
}

interface Choice { templateId: string; name: string; template: ProductTemplate }

/** The template's current version, as the compositor reads it. */
function wireVersion(t: ProductTemplate): WireTemplateVersion {
  const v = t.currentVersion;
  return {
    id: v.versionId, template_id: t.templateId, version: v.versionNumber,
    status: "Active", locked: true, payload: v.payload ?? ({} as WireTemplateVersion["payload"]),
  };
}

/** Enough to choose from in one list. */
const MAX_CHOICES = 40;

export function LightPreview({ values, makeDesign = sampleDesign }: { values: PreviewLight; makeDesign?: () => Promise<Uint8Array> }) {
  const { client, engine, io } = useServices();
  const [choices, setChoices] = useState<Choice[] | null>(null);
  const [picked, setPicked] = useState("");
  const [url, setUrl] = useState<string>();
  const [status, setStatus] = useState<"idle" | "downloading" | "failed">("idle");
  const [mode, setMode] = useState<"light" | "key" | "original">("light");
  /** A template downloaded for the preview, so a second render reads its full record. */
  const fetched = useRef(new Map<string, ProductTemplate>());
  const pan = usePanZoom();
  const valuesRef = useRef(values);
  valuesRef.current = values;

  // Owner, 2026-10-13: "just pick any template or give me option to select the template". Every
  // template with a published mock-up is offered; one that is not on this device is downloaded.
  useEffect(() => {
    let live = true;
    void (async () => {
      let found: Choice[] = [];
      try {
        found = (await client.listTemplates())
          .filter((t) => t.currentVersion?.versionId && (t.currentVersion.hasBakedAssets || t.currentVersion.bakedAssets))
          .slice(0, MAX_CHOICES)
          .map((t) => ({ templateId: t.templateId, name: t.name, template: t }));
      } catch { /* an empty list says the same thing */ }
      if (!live) return;
      setChoices(found);
      setPicked((p) => p || found[0]?.templateId || "");
    })();
    return () => { live = false; };
  }, [client]);

  const { print_shading, print_highlights, key_shadow_depth, key_highlight_depth, shading_smoothing, case_highlights, print_gradient, print_grain, print_sheen, print_black, print_white } = values;
  const keySig = JSON.stringify([values.key_by_purity, values.key_method, values.key_noise_floor, values.key_snap_low, values.key_snap_high, values.key_flat_sample, values.key_tolerance, values.key_purity_start, values.key_purity_full, values.key_dark_keep, values.key_softness, values.key_colour_removal]);
  useEffect(() => {
    const choice = choices?.find((c) => c.templateId === picked);
    if (!choice || !engine.composeMockup) return;
    let live = true;
    let made: string | undefined;
    // A slider fires on every step; the render is the expensive part.
    const timer = setTimeout(() => void (async () => {
      try {
        let template = fetched.current.get(choice.templateId) ?? choice.template;
        if (mode !== "light") {
          if (!caseImageRef(template)) {
            if (live) setStatus("downloading");
            template = await client.getTemplate(choice.templateId);
            fetched.current.set(choice.templateId, template);
          }
          const ref = caseImageRef(template);
          if (!ref) throw new Error("no case image");
          if (mode === "original") {
            // The print guide image as it came from Generate: the reference the key works on.
            if (live) { setUrl(ref); setStatus("idle"); }
            return;
          }
          made = URL.createObjectURL(await keyedOverSample(ref, valuesRef.current, await makeDesign()));
          if (live) { setUrl(made); setStatus("idle"); }
          return;
        }
        let stored = await getMockup(template.currentVersion.versionId, PRIMARY_VIEW);
        if (!stored) {
          if (live) setStatus("downloading");
          template = (await forceSyncTemplate(io, client, choice.templateId)).template;
          fetched.current.set(choice.templateId, template);
          stored = await getMockup(template.currentVersion.versionId, PRIMARY_VIEW);
        }
        if (!stored) throw new Error("no mock-up");
        const assets: MockupAssets = { ...stored, printShadingStrength: print_shading, shading: previewShading(stored.shading, valuesRef.current) };
        const job = { templateVersion: wireVersion(template), designs: [], views: [PRIMARY_VIEW], settings: { format: "png" } } as RenderBatchJob;
        const png = await engine.composeMockup!(composeArgsFor(job, assets, await makeDesign(), PRIMARY_VIEW));
        if (!png) throw new Error("no render");
        made = URL.createObjectURL(new Blob([png as BlobPart], { type: "image/png" }));
        if (live) { setUrl(made); setStatus("idle"); }
      } catch {
        if (live) setStatus("failed");
      }
    })(), 250);
    return () => {
      live = false;
      clearTimeout(timer);
      if (made) URL.revokeObjectURL(made);
    };
  }, [choices, picked, mode, keySig, engine, io, client, makeDesign, print_shading, print_highlights, key_shadow_depth, key_highlight_depth, shading_smoothing, case_highlights, print_gradient, print_grain, print_sheen, print_black, print_white]);

  if (choices === null) return null;
  if (!choices.length) {
    return (
      <p className="faint" data-testid="light-preview-empty">
        No template has a saved mock-up yet, so there is no preview. Save a template in the editor first.
      </p>
    );
  }
  return (
    <div className="light-preview" data-testid="light-preview">
      <div className="light-preview__zoom" role="group" aria-label="Preview">
        {(["light", "key", "original"] as const).map((m) => (
          <button key={m} type="button" className={`btn btn--sm${mode === m ? " is-active" : ""}`} aria-pressed={mode === m}
            onClick={() => setMode(m)} data-testid={`light-preview-mode-${m}`}>
            {m === "light" ? "Light" : m === "key" ? "Key" : "Original"}
          </button>
        ))}
      </div>
      <label className="setting-row">
        <span className="setting-row__label">Preview on</span>
        <select className="select" value={picked} data-testid="light-preview-template"
          onChange={(e) => setPicked(e.target.value)}>
          {choices.map((c) => <option key={c.templateId} value={c.templateId}>{c.name}</option>)}
        </select>
      </label>
      {status === "downloading" && <p className="faint" data-testid="light-preview-downloading">Downloading this template's mock-up…</p>}
      {status === "failed"
        ? <p className="faint" data-testid="light-preview-failed">This template could not be rendered. Choose another one.</p>
        : url && (
          <>
            <div className="light-preview__stage" data-testid="light-preview-stage" {...pan.handlers} onPointerCancel={pan.handlers.onPointerUp}>
              <img className="light-preview__img" src={url} alt="Preview of the print-area light" data-testid="light-preview-img" draggable={false}
                style={{ transform: `translate(${pan.view.x}px, ${pan.view.y}px) scale(${pan.view.zoom})` }} />
            </div>
            <div className="light-preview__zoom">
              <button type="button" className="btn btn--sm btn--ghost" onClick={() => pan.zoomBy(1 / 1.5)} disabled={pan.view.zoom <= MIN_ZOOM} data-testid="light-preview-zoom-out">−</button>
              <span className="faint">{Math.round(pan.view.zoom * 100)}%</span>
              <button type="button" className="btn btn--sm btn--ghost" onClick={() => pan.zoomBy(1.5)} disabled={pan.view.zoom >= MAX_ZOOM} data-testid="light-preview-zoom-in">+</button>
              <button type="button" className="btn btn--sm btn--ghost" onClick={pan.fit} data-testid="light-preview-fit">Fit</button>
            </div>
          </>
        )}
      <p className="faint">Light shows the saved mock-up with the light values. Key keys the template's print guide image with the key values, over the sample design. Original shows that print guide image as Generate made it. Both use the values on the right before you save them. Scroll or pinch to zoom; drag to move; double-click to fit.</p>
    </div>
  );
}
